/*
Similar to Split backend's ab_test and ab_finished.
abTest returns a promise for the bucket.

E.g.:
Aedu.Split.abTest("test_name", ["split_1", "split_2"]).then((bucket) => {
});
Aedu.Split.abFinished("test_name")

Weight:
  If you'd like to add weight, all options passed in requires a %.

Aedu.Split.abTest("test_name", [{split_1: 90}, {"split_2": 10}])
*/

import md5 from 'md5';
import { captureMessage } from '@sentry/browser';
import {
  getTestOverrideFromCookie,
  noticeUnrecordedTest,
} from 'js/admin_user_tests';
import queryParam from 'js/utils/queryParam';

export var Split = {
  pendingRequests: {},

  abTest(test, buckets, force = '') {
    if (this.pendingRequests[test]) {
      return this.pendingRequests[test];
    }

    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.pendingRequests[test] = new Promise((resolve, reject) => {
      const override = this.overrideBucket(test);
      if (override) {
        resolve(override);
        return;
      }

      $.ajax({
        type: 'POST',
        url: '/record_split',
        data: JSON.stringify({
          ab_test_name: test,
          variants: buckets,
          finish: false,
          force,
        }),
        contentType: 'application/json',

        success: (bucket) => {
          Kale.notify({
            collectionName: 'sutd_regpath_july_2018',
            eventData: {
              event: 'bucketed',
              component: test,
              component_action: bucket,
              step_name: bucket,
            },
          });
          resolve(bucket);
        },
      }).fail(() => {
        if (typeof buckets[0] === 'string') {
          resolve(buckets[0]);
        } else {
          resolve(Object.keys(buckets[0])[0]);
        }
      });
    });
    return this.pendingRequests[test];
  },

  // We do not support resetting tests on the front end
  abFinished(test) {
    return $.post('/record_split', {
      ab_test_name: test,
      finish: true,
    });
  },

  bucketNames(buckets) {
    return buckets.map((bucket) =>
      typeof bucket === 'string' ? bucket : Object.keys(bucket)[0],
    );
  },

  pickBucket(uniqueId, test, buckets) {
    const bucketNames = Split.bucketNames(buckets);

    const override = this.overrideBucket(test);
    if (override) {
      if (bucketNames.includes(override)) {
        return override;
      } else {
        captureMessage(`${override} is not a bucket in ${test}`);
      }
    }

    const weights = buckets.map((bucket) => {
      return typeof bucket === 'string' ? 1 : Object.values(bucket)[0];
    });
    const total = weights.reduce((sum, weight) => sum + weight);

    const hashVal = md5(`${uniqueId}${test}`);
    const deterministicNumber = parseInt(hashVal.slice(-12), 16);
    const random_ratio = (deterministicNumber % 100000) / 100000.0;

    let point = random_ratio * total;
    return bucketNames.find((bucket, i) => {
      if (weights[i] > point) {
        return true;
      } else {
        point -= weights[i];
        return false;
      }
    });
  },

  // Like in Split::Helper, this returns a bucket but doesn't write to user_tests.
  // Does not need to communicate with server unlike abTest.
  unrecordedAbTest(uniqueId, test, buckets) {
    const bucket = Split.pickBucket(uniqueId, test, buckets);
    const bucketNames = Split.bucketNames(buckets);
    noticeUnrecordedTest(test, bucketNames, bucket);
    return bucket;
  },

  // Hey so tests are all deterministic now, so this is a quick little
  // improvement we can make. We might want to just change abTest to work this
  // way, but let's start it out as a separate function and see how it does.
  pickAndRecordUserAbTest(userId, test, buckets) {
    const bucket = Split.pickBucket(userId, test, buckets);
    Split.abTest(test, buckets, bucket);
    return bucket;
  },

  // Used when you want multiple tests with shared bucketing logic.
  // The `bucketTest` will be used to bucket the user, but that bucketing
  // will be submitted under `recordTest`.
  pickAndRecordSharedUserAbTest(userId, bucketTest, recordTest, buckets) {
    const bucket = Split.unrecordedAbTest(userId, bucketTest, buckets);
    Split.abTest(recordTest, buckets, bucket);

    return bucket;
  },

  overrideBucket(test) {
    return queryParam(`ab_test[${test}]`) || getTestOverrideFromCookie(test);
  },
};

export default Split;
